import { useParams } from "react-router-dom";
import { useConnectedPlayer } from "../../Hooks/useConnectedPlayer.tsx";
import { useCallback, useMemo, useState } from "react";
import SearchInput from "./Components/SearchInput.js";
import LoadingSpinner from "../../Components/LoadingSpinner.js";
import { EmptyState } from "./Components/EmptyState.js";
import SectionHeader from "./Components/SectionHeader.js";
import PlaylistItem from "./Components/PlaylistItem.js";
import ScrollableContainer from "./Components/ScrollableContainer.js";
import {
  colors,
  typography,
  spacing,
  borderRadius,
} from "../../styles/theme.js";
import { useUserPlaylist } from "../../Hooks/useUserPlaylist.tsx";
import { useSongSearch } from "../../Hooks/useSongSearch.tsx";
import { cacheSongDataForPlaylist } from "../../Api/maintence/cacheSongData.js";
import { updatePlaylistDurationMap } from "../../Api/maintence/updateDurationMaps.js";
import PlaylistBuilderHeader from "./Components/PlaylistBuilderHeader.js";
import { RxCheck, RxCross2, RxPlus } from "react-icons/rx";
import { PiWarningCircleFill } from "react-icons/pi";
import { formatDurationSeconds } from "../../utils/formatDurationSeconds.js";
import { WarningComponent } from "../../Components/WarningComponent.js";

const refreshPlaylist = async (playlistId) => {
  await cacheSongDataForPlaylist({ recordName: "user_playlists", playlistId });
  await updatePlaylistDurationMap({
    recordName: "user_playlists",
    playlistId,
    shuffle: false,
  });
};

export const PlaylistBuilderScreen = () => {
  const { connectionId } = useParams();
  const { isValid, isLoading, userId } = useConnectedPlayer({ connectionId });
  console.log("///userId", userId);
  const {
    playlistId,
    playlistSongs,
    isLoading: isLoadingUserPlaylist,
    updateUserPlaylist,
  } = useUserPlaylist({
    userId,
  });

  if (!isValid && !isLoadingUserPlaylist) {
    return (
      <div style={styles.loadingContainer}>
        <WarningComponent
          message='Return to your device and select "Edit Playlist" to receive another link'
          header="Connection expired"
        />
      </div>
    );
  }

  if (isLoading || isLoadingUserPlaylist) {
    return (
      <div style={styles.loadingContainer}>
        <LoadingSpinner message="Loading playlist..." />
      </div>
    );
  }

  return (
    <>
      {playlistSongs && (
        <UserPlaylistContent
          playlistId={playlistId}
          playlistSongs={playlistSongs}
          updateUserPlaylist={updateUserPlaylist}
        />
      )}
    </>
  );
};

const UserPlaylistContent = ({
  playlistSongs,
  updateUserPlaylist,
  playlistId,
}) => {
  const [selectedSongs, setSelectedSongs] = useState(playlistSongs); // actual song objects

  const {
    search,
    setSearch,
    songResults,
    loading: loadingSearch,
  } = useSongSearch();

  const [loadingSave, setLoadingSaving] = useState(false);
  const [successfulSave, setSuccessfulSave] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const handleSearch = useCallback(
    (searchValue) => {
      setSaveMessage(null);
      setSaveError(null);
      setSearch(searchValue);
    },
    [setSaveMessage, setSaveError, setSearch]
  );

  const handleSelectSong = useCallback(
    (song) => {
      setSaveMessage(null);
      setSaveError(null);
      if (!selectedSongs.find((s) => s.id === song.id)) {
        setSelectedSongs([...selectedSongs, song]);
      }
    },
    [selectedSongs, setSaveMessage, setSaveError]
  );

  const handleRemoveSong = useCallback(
    (songId) => {
      setSaveMessage(null);
      setSaveError(null);
      setSelectedSongs(selectedSongs.filter((s) => s.id !== songId));
    },
    [selectedSongs, setSaveMessage, setSaveError]
  );

  const handleSave = useCallback(async () => {
    setLoadingSaving(true);
    updateUserPlaylist(selectedSongs.map((s) => s.id))
      .then(async () => {
        console.log("hit");
        await refreshPlaylist(playlistId);
      })
      .then(() => {
        setLoadingSaving(false);
        setSuccessfulSave(true);
        setTimeout(() => {
          setSuccessfulSave(false);
        }, 1000);
        setSaveMessage("Playlist saved successfully");
      })
      .catch((err) => {
        setLoadingSaving(false);
        setSuccessfulSave(false);
        setSaveError(err.message);
      });
  }, [updateUserPlaylist, selectedSongs, playlistId]);

  const isSelected = (songId) =>
    selectedSongs.find((s) => s.id === songId) !== undefined;

  return (
    <div style={styles.container}>
      <div style={styles.twoColumnLayout}>
        <div style={styles.playlistContainer}>
          <PlaylistBuilderHeader
            playlistId={playlistId}
            handleSave={handleSave}
            saveMessage={saveMessage}
            saveError={saveError}
            saving={loadingSave}
            selectedSongs={selectedSongs}
            successfulSave={successfulSave}
          />
          <SectionHeader
            title="Search and select songs to build your custom playlist"
            badge={
              selectedSongs.length > 0
                ? `${selectedSongs.length} song${selectedSongs.length !== 1 ? "s" : ""}`
                : null
            }
          />
          {selectedSongs.length === 0 ? (
            <EmptyState
              icon="🎵"
              message="No songs selected yet."
              subMessage="Search and add songs to get started"
            />
          ) : (
            <ScrollableContainer>
              {selectedSongs.map((song, index) => (
                <PlaylistItem
                  id={song.id}
                  key={song.id}
                  song={song}
                  index={index}
                  onActionPress={handleRemoveSong}
                  ActionIcon={RxCross2}
                  actionIconColor={colors.primary}
                  endContent={formatDurationSeconds(song.durationInSeconds)}
                />
              ))}
            </ScrollableContainer>
          )}
        </div>
        <div style={styles.searchContainer}>
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Search for songs..."
          />
          <SearchResults
            songResults={songResults}
            isSelected={isSelected}
            loading={loadingSearch}
            search={search}
            onRemove={handleRemoveSong}
            onSelect={handleSelectSong}
          />
        </div>
      </div>
    </div>
  );
};

const SearchResults = ({
  songResults,
  isSelected,
  loading,
  search,
  onRemove,
  onSelect,
}) => {
  const handleActionPress = useCallback(
    (songId) => {
      const song = songResults.find((s) => s.id === songId);
      if (!song) return;

      if (isSelected(songId)) {
        onRemove(songId);
      } else {
        onSelect(song);
      }
    },
    [songResults, isSelected, onRemove, onSelect]
  );

  const resultsMap = useMemo(() => {
    return songResults.map((song, index) => (
      <PlaylistItem
        id={song.id}
        key={song.id}
        song={song}
        index={index}
        ActionIcon={isSelected(song.id) ? RxCheck : RxPlus}
        actionIconColor={isSelected(song.id) ? colors.success : "black"}
        onActionPress={handleActionPress}
        endContent={formatDurationSeconds(song.durationInSeconds)}
      />
    ));
  }, [songResults, isSelected, handleActionPress]);

  const resultState = useMemo(() => {
    if (loading)
      return (
        <div style={styles.loadingContainer}>
          <LoadingSpinner message="Loading songs..." />
        </div>
      );
    if (songResults.length === 0)
      return (
        <EmptyState
          message={
            search
              ? "No songs found. Try a different search term."
              : "Start typing to search for songs..."
          }
        />
      );
    return resultsMap;
  }, [loading, resultsMap, search, songResults.length]);

  return (
    <div style={styles.searchResults}>
      <ScrollableContainer>{resultState}</ScrollableContainer>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    marginTop: 30,
    flexDirection: "column",
    width: 1000,
    margin: "20 auto 20px auto",
    fontFamily: typography.fontFamily,
    color: colors.text,
    minHeight: "90vh",
    borderRadius: borderRadius.lg,
    backgroundColor: colors.secondaryBackground,
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing.xxl,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    margin: "0 0 8px 0",
  },
  subtitle: {
    color: colors.textSecondary,
    margin: 0,
    fontSize: typography.fontSize.md,
  },
  playlistContainer: {
    color: colors.text,
    display: "flex",
    flexDirection: "column",
    flexBasis: "60%",
    maxWidth: "60%",
    flexGrow: 1,
    flexShrink: 0,
    minHeight: 0,
    overflow: "hidden",
    borderRight: `1px solid ${colors.secondary}`,
  },
  searchContainer: {
    paddingTop: spacing.lg,
    overflow: "hidden",
    backgroundColor: colors.secondaryBackground,
    color: colors.text,
    display: "flex",
    flexDirection: "column",
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: "40%",
    maxWidth: "40%",
    minHeight: 0,
  },
  twoColumnLayout: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  searchResults: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  saveButton: {
    marginTop: "8px",
    padding: "8px 16px",
    borderRadius: 4,
    border: "none",
    fontWeight: typography.fontWeight.bold,
    color: colors.white,
    background: colors.primary,
    fontSize: typography.fontSize.md,
    cursor: "pointer",
    opacity: 0.6,
    marginRight: 10,
    transition: "opacity 0.2s",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: 400,
    textAlign: "center",
    color: colors.text,
  },
};
